import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Clock, Plus, Trash2, Save, Calendar, ToggleLeft, ToggleRight,
  Package, ShoppingBag, AlertTriangle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DAYS = [
  { value: 0, label: 'Lundi' },
  { value: 1, label: 'Mardi' },
  { value: 2, label: 'Mercredi' },
  { value: 3, label: 'Jeudi' },
  { value: 4, label: 'Vendredi' },
  { value: 5, label: 'Samedi' },
  { value: 6, label: 'Dimanche' }
];

export default function BusinessPickupSlotsPage() {
  const navigate = useNavigate();
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingSlot, setEditingSlot] = useState(null);
  const [saving, setSaving] = useState(false);

  const emptySlot = {
    day_of_week: 0,
    start_time: '09:00',
    end_time: '12:00',
    max_orders: 5
  };

  const [formData, setFormData] = useState(emptySlot);

  useEffect(() => {
    fetchSlots();
  }, []);

  const fetchSlots = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/business/register');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/business/pickup-slots`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setSlots(await res.json());
      }
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const openAddDialog = () => {
    setEditingSlot(null);
    setFormData(emptySlot);
    setShowDialog(true);
  };

  const openEditDialog = (slot) => {
    setEditingSlot(slot);
    setFormData({
      day_of_week: slot.day_of_week,
      start_time: slot.start_time,
      end_time: slot.end_time,
      max_orders: slot.max_orders
    });
    setShowDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    const token = localStorage.getItem('token');
    try {
      const url = editingSlot 
        ? `${API_URL}/api/business/pickup-slots/${editingSlot.id}`
        : `${API_URL}/api/business/pickup-slots`;
      
      const res = await fetch(url, {
        method: editingSlot ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (!res.ok) throw new Error('Erreur');

      toast.success(editingSlot ? 'Créneau mis à jour' : 'Créneau créé');
      setShowDialog(false);
      fetchSlots();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const toggleSlot = async (slotId) => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/api/business/pickup-slots/${slotId}/toggle`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        setSlots(slots.map(s => s.id === slotId ? { ...s, is_active: data.is_active } : s));
        toast.success(data.is_active ? 'Créneau activé' : 'Créneau désactivé');
      }
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const deleteSlot = async (slotId) => {
    if (!window.confirm('Supprimer ce créneau ?')) return;

    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/api/business/pickup-slots/${slotId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        setSlots(slots.filter(s => s.id !== slotId));
        toast.success('Créneau supprimé');
      }
    } catch (error) {
      toast.error('Erreur');
    }
  };

  // Group slots by day
  const slotsByDay = DAYS.map(day => ({
    ...day,
    slots: slots.filter(s => s.day_of_week === day.value).sort((a, b) => a.start_time.localeCompare(b.start_time))
  }));

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" asChild>
              <Link to="/business/orders">
                <ArrowLeft className="w-5 h-5" />
              </Link>
            </Button>
            <h1 className="font-semibold text-charcoal flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Créneaux de retrait
            </h1>
          </div>
          <Button onClick={openAddDialog}>
            <Plus className="w-4 h-4 mr-2" />
            Ajouter un créneau
          </Button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Info */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6 flex items-start gap-3">
          <ShoppingBag className="w-5 h-5 text-blue-500 mt-0.5" />
          <div>
            <h3 className="font-medium text-blue-900">Click & Collect</h3>
            <p className="text-sm text-blue-700">
              Définissez vos créneaux de retrait disponibles. Les clients pourront réserver un créneau lors de leur commande.
            </p>
          </div>
        </div>

        {/* Slots by Day */}
        <div className="space-y-4">
          {slotsByDay.map((day) => (
            <div key={day.value} className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="px-4 py-3 bg-gray-50 border-b flex items-center justify-between">
                <h3 className="font-medium text-charcoal">{day.label}</h3>
                <span className="text-sm text-muted-foreground">
                  {day.slots.length} créneau{day.slots.length > 1 ? 'x' : ''}
                </span>
              </div>
              
              {day.slots.length === 0 ? (
                <div className="p-4 text-center text-muted-foreground text-sm">
                  Aucun créneau défini
                </div>
              ) : (
                <div className="divide-y">
                  {day.slots.map((slot) => (
                    <div key={slot.id} className="p-4 flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <button
                          onClick={() => toggleSlot(slot.id)}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            slot.is_active ? 'bg-green-500' : 'bg-gray-300'
                          }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              slot.is_active ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                        <div>
                          <p className={`font-medium ${slot.is_active ? 'text-charcoal' : 'text-muted-foreground'}`}>
                            {slot.start_time} - {slot.end_time}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            Max {slot.max_orders} commande{slot.max_orders > 1 ? 's' : ''}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="ghost" size="sm" onClick={() => openEditDialog(slot)}>
                          Modifier
                        </Button>
                        <Button variant="ghost" size="sm" className="text-red-500" onClick={() => deleteSlot(slot.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {slots.length === 0 && (
          <div className="text-center py-12">
            <Clock className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="font-semibold text-charcoal mb-2">Aucun créneau configuré</h3>
            <p className="text-muted-foreground mb-4">
              Ajoutez vos premiers créneaux de retrait pour activer le Click & Collect.
            </p>
            <Button onClick={openAddDialog}>
              <Plus className="w-4 h-4 mr-2" />
              Ajouter un créneau
            </Button>
          </div>
        )}
      </div>

      {/* Slot Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingSlot ? 'Modifier le créneau' : 'Nouveau créneau'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-1 block">Jour</label>
              <select
                value={formData.day_of_week}
                onChange={(e) => setFormData({ ...formData, day_of_week: parseInt(e.target.value) })}
                className="w-full px-3 py-2 border rounded-lg"
              >
                {DAYS.map(day => (
                  <option key={day.value} value={day.value}>{day.label}</option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-1 block">Heure de début</label>
                <Input
                  type="time"
                  value={formData.start_time}
                  onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Heure de fin</label>
                <Input
                  type="time"
                  value={formData.end_time}
                  onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                  required
                />
              </div>
            </div>

            <div>
              <label className="text-sm font-medium mb-1 block">Commandes max par créneau</label>
              <Input
                type="number"
                min="1"
                max="50"
                value={formData.max_orders}
                onChange={(e) => setFormData({ ...formData, max_orders: parseInt(e.target.value) })}
                required
              />
              <p className="text-xs text-muted-foreground mt-1">
                Nombre maximum de commandes que vous pouvez préparer pour ce créneau
              </p>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                Annuler
              </Button>
              <Button type="submit" disabled={saving}>
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Sauvegarde...' : 'Sauvegarder'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
